import {
  PAY_TYPE_OPTIONS,
  useDisburseBonusMutation,
  useGetAllBonusPoliciesQuery,
  useGetPayrollCyclesQuery,
  useGetSingleBonusPolicyQuery,
  type BonusPolicyItem,
  type PayType,
} from '@/app/features/employee/emp-payroll';
import IMAGES from '@/assets/images';
import CustomDrawer from '@/components/CustomDrawer';
import CustomImage from '@/components/CustomImage';
import EmptyState from '@/components/EmptyState';
import WithLoader from '@/components/WithLoader';
import { Calendar03Icon, ChevronDownIcon, UsersIcon, XIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { snakeToCamel } from '@/utils/string.utils';
import { Button, DatePicker, Flex, Form, InputNumber, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { formatBDT, toAmount } from '../shared/payroll.helpers';

type Props = {
  open: boolean;
  handleClose: () => void;
};

type FormValues = {
  policyId?: string;
  /** Which payroll batches receive the bonus. */
  payBasis: PayType;
  month?: Dayjs;
  /** DAILY only, when the month splits into several cycles. */
  cycleNo?: number;
  count: number;
};

/** The payroll populations a policy can be paid into. */
const basesFor = (policy?: BonusPolicyItem): PayType[] =>
  policy?.pay_basis === 'MONTHLY' || policy?.pay_basis === 'DAILY'
    ? [policy.pay_basis]
    : ['MONTHLY', 'DAILY'];

/** "৳5,000.00 · 2 times a fiscal year" under the chosen policy. */
const policySummary = (policy: BonusPolicyItem) => {
  const amount =
    toAmount(policy.amount_percentage) > 0
      ? `${toAmount(policy.amount_percentage)}% of ${snakeToCamel(policy.amount_percentage_of || '')}`
      : formatBDT(policy.amount_flat, formatBDT(0));
  const times = policy.count_per_fiscal_year || 0;
  return `${amount} · ${times} time${times === 1 ? '' : 's'} a fiscal year`;
};

/**
 * Pays one bonus policy into a salary month.
 *
 * Choose the policy, the payroll it goes into (monthly, or one daily cycle)
 * and the month; the server answers with who is eligible. Anyone can be left
 * out before paying. The bonus then shows up in each employee's payroll row.
 */
const DisburseBonus = ({ open, handleClose }: Props) => (
  <CustomDrawer
    width={800}
    open={open}
    onClose={handleClose}
    title="Disburse Bonus"
  >
    {/* Mounted only while open, so each disbursement starts clean. */}
    {open && <DisburseForm onDone={handleClose} />}
  </CustomDrawer>
);

const DisburseForm = ({ onDone }: { onDone: () => void }) => {
  const [form] = Form.useForm<FormValues>();
  const policyId = Form.useWatch('policyId', form);
  const month = Form.useWatch('month', form);
  const payBasis: PayType = Form.useWatch('payBasis', form) || 'MONTHLY';
  const isDaily = payBasis === 'DAILY';
  const isChosen = Boolean(policyId && month);

  /** Employees the admin took out of this disbursement. */
  const [excluded, setExcluded] = useState<string[]>([]);

  const { data: policyList, isLoading: policiesLoading } =
    useGetAllBonusPoliciesQuery({ params: {} });
  const policies = policyList?.data?.data || [];
  const selectedPolicy = policies.find((item) => item.id === policyId);
  const bases = basesFor(selectedPolicy);

  // A daily month can split into several cycles; the bonus goes into one.
  const { data: cyclesData, isFetching: cyclesLoading } =
    useGetPayrollCyclesQuery(
      {
        payBasis: 'DAILY',
        year: month?.year() ?? 0,
        month: month?.format('MM') ?? '',
      },
      { skip: !isDaily || !month },
    );
  const cycles = isDaily && month ? cyclesData?.data?.cycles || [] : [];
  const hasMultipleCycles = cycles.length > 1;

  // Eligibility depends on the month, so nothing is asked until both are set.
  const { data: detail, isFetching: eligibleLoading } =
    useGetSingleBonusPolicyQuery(
      {
        id: policyId as string,
        bonus_month: month?.format('MM'),
        bonus_year: month?.year(),
      },
      { skip: !isChosen },
    );
  const eligible = isChosen
    ? (detail?.data?.users || []).filter(
        (user) => !excluded.includes(user.user_id),
      )
    : [];

  const [disburseBonus, { isLoading: disbursing }] = useDisburseBonusMutation();

  const handleSubmit = async (values: FormValues) => {
    if (!values.policyId || !values.month || !eligible.length) return;
    try {
      await disburseBonus({
        policyId: values.policyId,
        count: values.count,
        month: values.month.format('MM'),
        year: values.month.year(),
        payBasis: values.payBasis,
        // Without a cycle the server pays every daily batch of the month.
        ...(values.payBasis === 'DAILY' && values.cycleNo
          ? { cycleNo: Number(values.cycleNo) }
          : {}),
        userIds: eligible.map((user) => user.user_id),
      }).unwrap();
      toast('success', 'Bonus disbursed successfully');
      onDone();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to disburse bonus'));
    }
  };

  return (
    <Form
      form={form}
      size="large"
      layout="vertical"
      className="custom-form space-y-6"
      initialValues={{ count: 1, payBasis: 'MONTHLY' }}
      onValuesChange={(changed: Partial<FormValues>) => {
        // Another policy or month has another eligible list.
        if ('policyId' in changed || 'month' in changed) setExcluded([]);

        // A policy for one population can only be paid into that payroll.
        if ('policyId' in changed) {
          const policy = policies.find((item) => item.id === changed.policyId);
          form.setFieldsValue({ payBasis: basesFor(policy)[0] });
        }

        // The cycle belongs to one month of the daily payroll.
        if (
          'policyId' in changed ||
          'month' in changed ||
          'payBasis' in changed
        ) {
          form.setFieldValue('cycleNo', undefined);
        }
      }}
      onFinish={handleSubmit}
    >
      <Form.Item
        label="Bonus Policy"
        name="policyId"
        rules={[{ required: true, message: 'Please select bonus policy' }]}
        extra={
          selectedPolicy && (
            <span className="text-white/50 text-xs">
              {policySummary(selectedPolicy)}
            </span>
          )
        }
      >
        <Select
          showSearch
          placeholder="Select"
          optionFilterProp="label"
          loading={policiesLoading}
          suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
          options={policies.map((item) => ({
            value: item.id,
            label: item.title,
          }))}
        />
      </Form.Item>

      <div className="grid grid-cols-2 gap-6">
        <Form.Item
          label="Pay Into"
          name="payBasis"
          rules={[{ required: true, message: 'Please select pay basis' }]}
          extra={
            bases.length === 1 && selectedPolicy ? (
              <span className="text-white/40 text-xs">
                This policy is for {bases[0].toLowerCase()} employees only
              </span>
            ) : undefined
          }
        >
          <Select
            suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
            options={PAY_TYPE_OPTIONS.map((option) => ({
              value: option.value,
              label: `${option.label} Payroll`,
              disabled: !bases.includes(option.value),
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Pay With Salary Of"
          name="month"
          rules={[{ required: true, message: 'Please select month' }]}
        >
          <DatePicker
            picker="month"
            format="MMMM YYYY"
            placeholder="Select month"
            className="w-full"
            suffixIcon={<Calendar03Icon className="text-white" />}
          />
        </Form.Item>

        {/* Several daily cycles in the month: pick the one to pay into. */}
        {hasMultipleCycles && (
          <Form.Item
            label="Cycle"
            name="cycleNo"
            rules={[{ required: true, message: 'Please select cycle' }]}
          >
            <Select
              placeholder="Select"
              loading={cyclesLoading}
              suffixIcon={<ChevronDownIcon className="text-white" size={24} />}
              options={cycles.map((cycle) => ({
                value: cycle.cycleNo,
                label: `Cycle ${cycle.cycleNo}: ${cycle.label}`,
                // A cycle that has not started has no payroll yet.
                disabled: dayjs(cycle.startDate).isAfter(dayjs(), 'day'),
              }))}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Times To Pay"
          name="count"
          rules={[{ required: true, message: 'Please enter times to pay' }]}
          extra={
            <span className="text-white/40 text-xs">
              How many of the year's bonuses this pays. Usually 1.
            </span>
          }
        >
          <InputNumber
            min={1}
            precision={0}
            controls={false}
            className="w-full"
          />
        </Form.Item>
      </div>

      {/* Who will receive it */}
      <div className="space-y-3">
        <h6 className="text-base text-white/60 font-medium border-b border-theme-gray-700 pb-3">
          Eligible Employees
          {eligible.length > 0 && (
            <span className="text-theme-primary"> ({eligible.length})</span>
          )}
        </h6>

        <WithLoader isLoading={eligibleLoading} className="rounded-lg">
          {eligible.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {eligible.map((user) => (
                <Flex
                  key={user.user_id}
                  gap={10}
                  className="p-2.5 rounded-lg bg-theme-slate-800 relative"
                >
                  <button
                    type="button"
                    title="Leave out"
                    onClick={() =>
                      setExcluded((prev) => [...prev, user.user_id])
                    }
                    className="absolute -right-1 -top-1 p-0.5 bg-theme-primary rounded-full"
                  >
                    <XIcon size={12} strokeWidth={3} />
                  </button>
                  <CustomImage
                    alt="User"
                    width={25}
                    height={25}
                    src={user.photo || IMAGES.avatar}
                    className="rounded-full flex-shrink-0"
                  />
                  <div className="overflow-hidden min-w-0 flex-1">
                    <span className="block text-xs text-white/70">
                      ID #{user.employee_id}
                    </span>
                    <span className="block text-base font-medium truncate">
                      {user.name}
                    </span>
                    <span className="block text-xs text-white/50">
                      Disbursed Count: {user.disbursed_count ?? 0}
                    </span>
                  </div>
                </Flex>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<UsersIcon size={36} />}
              className="py-8"
              title={
                isChosen ? 'No Eligible Employees' : 'Choose a Policy and Month'
              }
              description={
                isChosen
                  ? 'No employees are eligible for this bonus in the selected month.'
                  : 'Eligible employees appear here once both are selected.'
              }
            />
          )}
        </WithLoader>
      </div>

      <Flex justify="end">
        <Button
          htmlType="submit"
          loading={disbursing}
          disabled={!eligible.length}
          className="btn-primary min-w-[80px] h-11 font-semibold"
        >
          Disburse Bonus
        </Button>
      </Flex>
    </Form>
  );
};

export default DisburseBonus;
